import Image from 'next/image';

import { Box, Flex, Heading, Text } from '@chakra-ui/react';

import { SectionHeader } from '~/features/experiments/ab_plp_redesign_2025/components/section-header';
import { BookIcon } from '~/features/experiments/ab_plp_redesign_2025/icons';

const STATS_GRADIENT = 'linear-gradient(65.49deg, #031643 0%, #00298C 50%, #007D9F 100%)';
const STRIPES_SRC = '/images/experiments/ab_plp_redesign_2025/diagonal-lines.svg';

const STATS = [
  { value: '16.9', suffix: 'M+', label: 'registered users in more than 240 countries' },
  { value: '2.6', suffix: 'M+', label: 'Projects submitted' },
  { value: '205', suffix: 'k', label: 'Nanodegree certificates earned' },
  { value: '73', suffix: '%', label: 'Graduates reported a favorable career change' },
] as const;

const LOGOS = [
  { src: '/images/about-us/logo-github.svg', alt: 'GitHub', width: 96, height: 26 },
  { src: '/images/about-us/logo-accenture.svg', alt: 'Accenture', width: 144, height: 40 },
  { src: '/images/about-us/logo-nvidia.svg', alt: 'NVIDIA', width: 142, height: 26 },
  { src: '/images/about-us/logo-bmw.svg', alt: 'BMW', width: 40, height: 40 },
  { src: '/images/about-us/logo-microsoft.svg', alt: 'Microsoft', width: 132, height: 32 },
  { src: '/images/about-us/logo-amazon.svg', alt: 'Amazon', width: 99, height: 35 },
] as const;

export function IntroSection() {
  return (
    <Box as='section' bg='grey.50' py={{ base: 12, md: '6rem' }}>
      <Flex align='center' direction='column' gap={{ base: 10, md: '4.5rem' }} maxW='1056px' mx='auto'>
        <SectionHeader
          color='#5A5C62'
          containerProps={{ maxW: '836px', mx: 'auto', px: { base: 4, md: 0 }, width: 'full' }}
          iconSlot={<BookIcon color='#5A5C62' height='18px' width='18px' />}
          subtitle='About udacity'
          descriptionSlot={
            <Flex direction='column' gap={4}>
              <Text
                color='#0A0B0F'
                fontFamily='body'
                fontSize={{ base: '1rem', md: '1.125rem' }}
                fontWeight={600}
                lineHeight={1.5}
                textAlign='center'
              >
                Udacity began when Stanford instructors Sebastian Thrun and Peter Norvig had a revolutionary idea to
                offer their &ldquo;Introduction to Artificial Intelligence&rdquo; course online&mdash;to anyone, for
                free.
              </Text>
              <Text
                color='#5A5C62'
                fontFamily='body'
                fontSize={{ base: '0.9375rem', md: '1rem' }}
                lineHeight={1.5}
                textAlign='center'
              >
                Over 160,000 students in more than 190 countries enrolled, demanding a new way to deliver education. A
                startling discovery emerged: The top 400 students weren&rsquo;t from Stanford. This revelation sparked a
                mission to make lifelong learning more equitable and inclusive. Udacity has been expanding opportunities
                ever since. Udacity is now part of Accenture.
              </Text>
            </Flex>
          }
          titleSlot={
            <Heading
              as='h2'
              color='#0A0B0F'
              fontFamily='heading'
              fontSize={{ base: '2rem', md: '3.375rem' }}
              fontWeight={600}
              letterSpacing='-0.02em'
              lineHeight={1.1}
              textAlign='center'
            >
              From democratizing education to talent transformation
            </Heading>
          }
        />

        <Box
          borderRadius={{ base: 0, md: '12px' }}
          mb={{ base: -12, md: 0 }}
          mt={{ base: 2, md: 0 }}
          overflow='hidden'
          pb={{ base: 16, md: 12 }}
          position='relative'
          pt={{ base: 16, md: 12 }}
          px={{ base: 6, md: 12 }}
          style={{ backgroundImage: STATS_GRADIENT }}
          width='full'
        >
          <Box height='584px' mixBlendMode='overlay' position='absolute' right={0} top={0} width='160px'>
            <Image
              alt=''
              loading='lazy'
              quality={75}
              src={STRIPES_SRC}
              style={{
                objectFit: 'contain',
                objectPosition: 'right',
                pointerEvents: 'none',
                transform: 'rotate(-90deg) scale(4) translate(20px, -15px)',
              }}
              fill
            />
          </Box>

          <Flex direction='column' gap={{ base: 8, md: 10 }} position='relative'>
            <Flex direction='column' gap={2}>
              <Heading
                as='h3'
                color='white'
                fontFamily='heading'
                fontSize={{ base: '1.5rem', md: '2.25rem' }}
                fontWeight={600}
                letterSpacing='-0.01em'
                lineHeight={1.1}
              >
                Curriculum developed by the best
              </Heading>
              <Text color='blue.100' fontFamily='body' fontSize='1rem'>
                Learn from instructors who work at tech&rsquo;s top companies.
              </Text>
            </Flex>

            <Flex
              alignItems='flex-end'
              borderTop='1px solid rgba(255,255,255,0.24)'
              flexWrap='wrap'
              gap={{ base: 6, md: 4 }}
              justifyContent={{ base: 'center', md: 'space-between' }}
              pt={10}
              px={2}
            >
              {LOGOS.map((logo) => (
                <Image key={logo.src} alt={logo.alt} height={logo.height} src={logo.src} width={logo.width} />
              ))}
            </Flex>

            <Flex flexWrap={{ base: 'wrap', md: 'nowrap' }} gap={{ base: 4, md: 4 }}>
              {STATS.map((stat) => (
                <Box
                  key={stat.label}
                  bg='rgba(123,162,255,0.08)'
                  border='1px solid rgba(255,255,255,0.56)'
                  borderRadius={{ base: '6px', md: '8px' }}
                  flex={{ base: '1 1 100%', md: '1 1 0' }}
                  minW={{ base: 'full', md: 0 }}
                  p={{ base: 4, md: 6 }}
                  pt={{ base: 4, md: 8 }}
                >
                  <Flex
                    align={{ base: 'center', md: 'flex-start' }}
                    direction={{ base: 'row', md: 'column' }}
                    gap={{ base: 6, md: 4 }}
                  >
                    <Flex align='baseline' gap={1} shrink={0} w={{ base: '7.5rem', md: 'auto' }}>
                      <Text
                        color='#89E6FF'
                        fontFamily='heading'
                        fontSize={{ base: '2.625rem', md: '2.875rem' }}
                        fontWeight={600}
                        lineHeight={1}
                      >
                        {stat.value}
                      </Text>
                      <Text
                        color='#89E6FF'
                        fontFamily='heading'
                        fontSize={{ base: '1.875rem', md: '2rem' }}
                        fontWeight={600}
                        lineHeight={1}
                      >
                        {stat.suffix}
                      </Text>
                    </Flex>
                    <Text
                      color='white'
                      flex={{ base: '1 0 0', md: 'auto' }}
                      fontFamily='body'
                      fontSize='0.8125rem'
                      fontWeight={500}
                      lineHeight={1.4}
                    >
                      {stat.label}
                    </Text>
                  </Flex>
                </Box>
              ))}
            </Flex>
          </Flex>
        </Box>
      </Flex>
    </Box>
  );
}
